import React, { useState, useRef, useEffect } from 'react'
import { X, FileSignature, CheckCircle, Download, Eye, EyeOff } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card } from '../ui/Card'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'

interface DocumentSigningModalProps {
  documentName: string
  documentType: 'exchange-agreement' | 'w9'
  exchangerData: {
    names: string
    homeAddress: string
    street?: string
    city?: string
    state?: string
    zipCode?: string
    taxIdNumber: string
  }
  buyerData?: {
    names: string
  }
  closingCompanyData?: {
    name: string
    address: string
    phone: string
    closerName: string
    closerEmail: string
  }
  propertyData?: {
    addressOrLegalDescription: string
  }
  onSign: (signature: string) => void
  onClose: () => void
  viewOnly?: boolean
}

export const DocumentSigningModal: React.FC<DocumentSigningModalProps> = ({
  documentName,
  documentType,
  exchangerData,
  buyerData,
  closingCompanyData,
  propertyData,
  onSign,
  onClose,
  viewOnly = false
}) => {
  useBodyScrollLock(true)
  const [signature, setSignature] = useState('')
  const [isDrawing, setIsDrawing] = useState(false)
  const [signatureType, setSignatureType] = useState<'type' | 'draw'>('type')
  const [showSSN, setShowSSN] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [hasDrawn, setHasDrawn] = useState(false)

  // Load saved signature on mount
  useEffect(() => {
    const savedSignature = localStorage.getItem('user_saved_signature')
    const savedSignatureType = localStorage.getItem('user_saved_signature_type') as 'type' | 'draw' | null
    
    if (savedSignature && savedSignatureType) {
      setSignatureType(savedSignatureType)
      
      if (savedSignatureType === 'type') {
        setSignature(savedSignature)
      } else if (savedSignatureType === 'draw') {
        // Load drawn signature onto canvas
        setTimeout(() => {
          if (canvasRef.current) {
            const canvas = canvasRef.current
            const ctx = canvas.getContext('2d')
            if (ctx) {
              const img = new Image()
              img.onload = () => {
                ctx.drawImage(img, 0, 0)
                setHasDrawn(true)
              }
              img.src = savedSignature
            }
          }
        }, 100)
      }
    }
  }, [])

  useEffect(() => {
    if (signatureType === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.strokeStyle = '#1e40af'
        
        // Restore drawn signature if switching back to draw mode
        const savedSignature = localStorage.getItem('user_saved_signature')
        const savedSignatureType = localStorage.getItem('user_saved_signature_type')
        if (savedSignature && savedSignatureType === 'draw') {
          const img = new Image()
          img.onload = () => {
            ctx.drawImage(img, 0, 0)
            setHasDrawn(true)
          }
          img.src = savedSignature
        }
      }
    }
  }, [signatureType])

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true)
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.beginPath()
      ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
    }
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    setHasDrawn(true)
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
      ctx.stroke()
    }
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      setHasDrawn(false)
    }
  }

  const clearSignature = () => {
    setSignature('')
    clearCanvas()
    localStorage.removeItem('user_saved_signature')
    localStorage.removeItem('user_saved_signature_type')
  }

  const handleSign = () => {
    if (signatureType === 'type' && signature.trim()) {
      // Save signature for reuse
      localStorage.setItem('user_saved_signature', signature.trim())
      localStorage.setItem('user_saved_signature_type', 'type')
      onSign(signature.trim())
    } else if (signatureType === 'draw' && hasDrawn && canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL()
      // Save signature for reuse
      localStorage.setItem('user_saved_signature', dataUrl)
      localStorage.setItem('user_saved_signature_type', 'draw')
      onSign(dataUrl)
    }
  }

  const canSubmit = signatureType === 'type' ? signature.trim().length > 0 : hasDrawn

  return (
    <div className="modal-overlay modal-overlay-nested">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between bg-white dark:bg-slate-800">
          <div className="flex items-center space-x-3">
            {viewOnly ? (
              <>
                <Eye className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">View {documentName}</h2>
              </>
            ) : (
              <>
                <FileSignature className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Sign {documentName}</h2>
              </>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-scroll pr-2 min-h-0 p-6 space-y-6" style={{ maxHeight: '100%' }}>
          {/* Document Preview with Prepopulated Fields */}
          <Card className="p-6 bg-slate-50 dark:bg-slate-900/50">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Document Preview</h3>
            
            {documentType === 'exchange-agreement' && (
              <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
                <div className="text-center mb-6">
                  <h4 className="text-xl font-bold text-slate-900 dark:text-slate-100">1031 EXCHANGE AGREEMENT</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Qualified Intermediary Services</p>
                </div>
                
                <p className="leading-relaxed">
                  This Exchange Agreement ("Agreement") is entered into on {new Date().toLocaleDateString()} 
                  between <strong>{exchangerData.names}</strong> ("Exchanger") and Open1031 Platform ("Qualified Intermediary").
                </p>

                <div className="border-l-4 border-primary-500 pl-4 bg-white dark:bg-slate-800 p-3 rounded">
                  <h5 className="font-semibold mb-2">EXCHANGER INFORMATION</h5>
                  <div className="space-y-1 text-xs">
                    <p><span className="font-medium">Name:</span> {exchangerData.names}</p>
                    <p>
                      <span className="font-medium">Address:</span>{' '}
                      {exchangerData.street && exchangerData.city && exchangerData.state && exchangerData.zipCode
                        ? `${exchangerData.street}, ${exchangerData.city}, ${exchangerData.state} ${exchangerData.zipCode}`
                        : exchangerData.homeAddress}
                    </p>
                    <p><span className="font-medium">Tax ID:</span> {exchangerData.taxIdNumber.replace(/./g, '*').slice(0, -4) + exchangerData.taxIdNumber.slice(-4)}</p>
                  </div>
                </div>

                {propertyData && (
                  <div className="border-l-4 border-blue-500 pl-4 bg-white dark:bg-slate-800 p-3 rounded">
                    <h5 className="font-semibold mb-2">RELINQUISHED PROPERTY</h5>
                    <p className="text-xs break-words whitespace-normal">{propertyData.addressOrLegalDescription}</p>
                  </div>
                )}

                {buyerData && (
                  <div className="border-l-4 border-green-500 pl-4 bg-white dark:bg-slate-800 p-3 rounded">
                    <h5 className="font-semibold mb-2">BUYER</h5>
                    <p className="text-xs">{buyerData.names}</p>
                  </div>
                )}

                <div className="space-y-2 text-xs">
                  <p className="font-semibold">KEY TERMS:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>The Qualified Intermediary agrees to facilitate the tax-deferred exchange pursuant to Section 1031 of the Internal Revenue Code</li>
                    <li>The Exchanger assigns all rights to the Relinquished Property sale proceeds to the Qualified Intermediary</li>
                    <li>Exchange must be completed within 180 days of the Relinquished Property sale</li>
                    <li>Replacement properties must be identified within 45 days</li>
                    <li>All exchange funds will be held in a segregated qualified escrow account</li>
                  </ul>
                </div>

                <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                  By signing below, the Exchanger acknowledges they have read, understand, and agree to the terms of this Exchange Agreement.
                </p>

                <div className="border-t-2 border-slate-200 dark:border-slate-700 pt-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-400 block mb-2">Exchanger Signature:</label>
                      {(signature || hasDrawn) ? (
                        <div className="p-3 bg-white dark:bg-slate-900 border-2 border-primary-500 dark:border-primary-600 rounded min-h-[60px] flex items-center">
                          {signatureType === 'type' ? (
                            <p className="text-2xl font-signature text-primary-900 dark:text-primary-100" style={{ fontFamily: 'cursive' }}>
                              {signature}
                            </p>
                          ) : (
                            canvasRef.current && (
                              <img 
                                src={canvasRef.current.toDataURL()} 
                                alt="Signature" 
                                className="max-h-[50px] object-contain"
                              />
                            )
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 dark:text-slate-400 italic">Signature will be captured below</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-400 block mb-2">Date:</label>
                      <p className="text-base font-medium pt-2">{new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {documentType === 'w9' && (
              <div className="space-y-3 text-sm text-slate-900 dark:text-slate-300 bg-white dark:bg-slate-800 p-6 border-2 border-slate-900 dark:border-slate-700">
                <div className="flex justify-between items-start border-b-2 border-slate-900 dark:border-slate-700 pb-2">
                  <div>
                    <h4 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Form W-9</h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400">(Rev. October 2018)</p>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Department of the Treasury<br/>Internal Revenue Service</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">Request for Taxpayer</p>
                    <p className="text-sm font-bold">Identification Number and Certification</p>
                    <p className="text-xs mt-1">▶ Go to <span className="underline">www.irs.gov/FormW9</span> for instructions and the latest information.</p>
                  </div>
                </div>

                <div className="space-y-3 mt-4">
                  <div className="border border-slate-900 dark:border-slate-600 p-2">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-400">1  Name (as shown on your income tax return). Name is required on this line; do not leave this line blank.</label>
                    <p className="font-medium mt-1 text-base border-b border-slate-400 pb-1">{exchangerData.names}</p>
                  </div>

                  <div className="border border-slate-900 dark:border-slate-600 p-2">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-400">2  Business name/disregarded entity name, if different from above</label>
                    <p className="text-slate-400 italic mt-1 border-b border-slate-400 pb-1">(blank)</p>
                  </div>

                  <div className="border border-slate-900 dark:border-slate-600 p-2">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-400 mb-2 block">3  Check appropriate box for federal tax classification of the person whose name is entered on line 1. Check only one of the following seven boxes.</label>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-slate-900 dark:border-slate-600 flex items-center justify-center bg-slate-900 dark:bg-slate-100">
                          <span className="text-white dark:text-slate-900 font-bold">✓</span>
                        </div>
                        <span>Individual/sole proprietor or single-member LLC</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-slate-900 dark:border-slate-600"></div>
                        <span>C Corporation</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-slate-900 dark:border-slate-600"></div>
                        <span>S Corporation</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-slate-900 dark:border-slate-600"></div>
                        <span>Partnership</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-slate-900 dark:border-slate-600"></div>
                        <span>Trust/estate</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-slate-900 dark:border-slate-600"></div>
                        <span>Limited liability company. Enter the tax classification (C=C corporation, S=S corporation, P=Partnership) ▶</span>
                      </div>
                    </div>
                    <p className="text-xs mt-2 text-slate-600 dark:text-slate-400 italic">Note: Check the appropriate box in the line above for the tax classification of the single-member owner. Do not check LLC if the LLC is classified as a single-member LLC that is disregarded from the owner unless the owner of the LLC is another LLC that is not disregarded from the owner for U.S. federal tax purposes. Otherwise, a single-member LLC that is disregarded from the owner should check the appropriate box for the tax classification of its owner.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-slate-900 dark:border-slate-600 p-2">
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-400">4  Exemptions (codes apply only to certain entities, not individuals; see instructions on page 3):</label>
                      <div className="mt-1">
                        <p className="text-xs">Exempt payee code (if any) <span className="border-b border-slate-400 inline-block w-16 ml-1">&nbsp;</span></p>
                      </div>
                    </div>
                    <div className="border border-slate-900 dark:border-slate-600 p-2">
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-400">&nbsp;</label>
                      <div className="mt-1">
                        <p className="text-xs">Exemption from FATCA reporting code (if any) <span className="border-b border-slate-400 inline-block w-16 ml-1">&nbsp;</span></p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-slate-900 dark:border-slate-600 p-2">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-400">5  Address (number, street, and apt. or suite no.) See instructions.</label>
                    <p className="font-medium mt-1 border-b border-slate-400 pb-1">
                      {exchangerData.street || exchangerData.homeAddress || ''}
                    </p>
                  </div>

                  <div className="border border-slate-900 dark:border-slate-600 p-2">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-400">6  City, state, and ZIP code</label>
                    <p className="font-medium mt-1 border-b border-slate-400 pb-1">
                      {exchangerData.city && exchangerData.state && exchangerData.zipCode
                        ? `${exchangerData.city}, ${exchangerData.state} ${exchangerData.zipCode}`
                        : ''}
                    </p>
                  </div>

                  <div className="border border-slate-900 dark:border-slate-600 p-2">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-400">7  List account number(s) here (optional)</label>
                    <p className="mt-1 border-b border-slate-400 pb-1">&nbsp;</p>
                  </div>

                  <div className="border-2 border-slate-900 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-900">
                    <p className="text-xs font-bold mb-2">Part I      Taxpayer Identification Number (TIN)</p>
                    <p className="text-xs mb-2">Enter your TIN in the appropriate box. The TIN provided must match the name given on line 1 to avoid backup withholding. For individuals, this is generally your social security number (SSN). However, for a resident alien, sole proprietor, or disregarded entity, see the instructions for Part I, later. For other entities, it is your employer identification number (EIN). If you do not have a number, see How to get a TIN, later.</p>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold">Social security number</p>
                          <button
                            type="button"
                            onClick={() => setShowSSN(!showSSN)}
                            className="group flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 hover:bg-primary-200 dark:bg-primary-800 dark:hover:bg-primary-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-primary-300 dark:border-primary-600"
                            title={showSSN ? 'Hide SSN' : 'Show SSN'}
                          >
                            {showSSN ? (
                              <>
                                <EyeOff className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                                <span className="text-xs font-medium text-primary-700 dark:text-primary-300">Hide</span>
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                                <span className="text-xs font-medium text-primary-700 dark:text-primary-300">Show</span>
                              </>
                            )}
                          </button>
                        </div>
                        <div className="flex items-center justify-center space-x-1 font-mono text-lg bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-600 p-2">
                          {showSSN ? (
                            <>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(0) || '•'}</span>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(1) || '•'}</span>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(2) || '•'}</span>
                              <span className="font-bold">-</span>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(4) || '•'}</span>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(5) || '•'}</span>
                              <span className="font-bold">-</span>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(7) || '•'}</span>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(8) || '•'}</span>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(9) || '•'}</span>
                              <span className="px-2 border border-slate-400">{exchangerData.taxIdNumber.charAt(10) || '•'}</span>
                            </>
                          ) : (
                            <>
                              <span className="px-2 border border-slate-400">•</span>
                              <span className="px-2 border border-slate-400">•</span>
                              <span className="px-2 border border-slate-400">•</span>
                              <span className="font-bold">-</span>
                              <span className="px-2 border border-slate-400">•</span>
                              <span className="px-2 border border-slate-400">•</span>
                              <span className="font-bold">-</span>
                              <span className="px-2 border border-slate-400">•</span>
                              <span className="px-2 border border-slate-400">•</span>
                              <span className="px-2 border border-slate-400">•</span>
                              <span className="px-2 border border-slate-400">•</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold mb-1">or</p>
                        <p className="text-xs font-semibold mb-1">Employer identification number</p>
                        <div className="flex items-center justify-center space-x-1 font-mono text-lg bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-600 p-2">
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                          <span className="font-bold">-</span>
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                          <span className="px-2 border border-slate-400">&nbsp;</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-2 border-slate-900 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-900">
                    <p className="text-xs font-bold mb-2">Part II     Certification</p>
                    <p className="text-xs leading-relaxed">Under penalties of perjury, I certify that:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2 text-xs mt-2">
                      <li>The number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me); and</li>
                      <li>I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding; and</li>
                      <li>I am a U.S. citizen or other U.S. person (defined below); and</li>
                      <li>The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.</li>
                    </ol>
                    <p className="text-xs mt-3 font-semibold">Certification instructions. You must cross out item 2 above if you have been notified by the IRS that you are currently subject to backup withholding because you have failed to report all interest and dividends on your tax return.</p>
                  </div>

                  <div className="border border-slate-900 dark:border-slate-600 p-3 bg-blue-50 dark:bg-blue-900/20">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">Sign Here ▶</label>
                        {(signature || hasDrawn) ? (
                          <div className="mt-2 p-3 bg-white dark:bg-slate-800 border-2 border-blue-500 dark:border-blue-600 rounded min-h-[60px] flex items-center justify-center">
                            {signatureType === 'type' ? (
                              <p className="text-2xl font-signature text-blue-900 dark:text-blue-100" style={{ fontFamily: 'cursive' }}>
                                {signature}
                              </p>
                            ) : (
                              canvasRef.current && (
                                <img 
                                  src={canvasRef.current.toDataURL()} 
                                  alt="Signature" 
                                  className="max-h-[50px] object-contain"
                                />
                              )
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-600 dark:text-slate-400 italic">Signature will be captured below</p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">Date ▶</label>
                        <p className="font-medium">{new Date().toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Signature Section */}
          {!viewOnly && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Your Signature</h3>
              <div className="flex items-center gap-3">
                {(signature || hasDrawn) && (
                  <>
                    <span className="text-xs text-green-600 dark:text-green-400 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Will be saved for future documents
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearSignature}
                      className="text-xs"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Signature Type Toggle */}
            <div className="flex gap-2 mb-4">
              <Button
                type="button"
                variant={signatureType === 'type' ? 'default' : 'outline'}
                onClick={() => setSignatureType('type')}
                size="sm"
              >
                Type Signature
              </Button>
              <Button
                type="button"
                variant={signatureType === 'draw' ? 'default' : 'outline'}
                onClick={() => setSignatureType('draw')}
                size="sm"
              >
                Draw Signature
              </Button>
            </div>

            {signatureType === 'type' ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Type your full name
                </label>
                <Input
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Enter your full legal name"
                  className="text-2xl font-signature"
                  style={{ fontFamily: 'cursive' }}
                />
                {signature && (
                  <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border-2 border-primary-200 dark:border-primary-800">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Preview:</p>
                    <p className="text-3xl font-signature" style={{ fontFamily: 'cursive' }}>{signature}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Draw your signature
                </label>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  className="w-full border-2 border-slate-300 dark:border-slate-600 rounded-lg cursor-crosshair bg-white"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearCanvas}
                >
                  Clear
                </Button>
              </div>
            )}

            <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-xs text-slate-600 dark:text-slate-400">
                  <p className="font-medium mb-1">Electronic Signature Consent</p>
                  <p>By clicking "Sign Document" below, you agree that your electronic signature is the legal equivalent of your manual signature on this document.</p>
                </div>
              </div>
            </div>
          </Card>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-slate-50 dark:bg-slate-900">
          <div className="flex justify-end space-x-3">
            {viewOnly ? (
              <Button onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleSign} disabled={!canSubmit}>
                  <FileSignature className="h-4 w-4 mr-2" />
                  Sign Document
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}




